import { ElementRef, EventEmitter, inject, Injectable, type OnDestroy } from '@angular/core';
import { fromEvent, type Observable, type Subscription } from 'rxjs';

@Injectable()
export class ScrollIntoViewControl implements OnDestroy {
  private _element = inject(ElementRef);

  initialized: boolean;

  private _eventEmitter: EventEmitter<void> = new EventEmitter<void>();
  private _intersectionObserver?: IntersectionObserver;
  private printSubscription: Subscription;

  // whether or no to keep checking after an intersection was found (useful for infinite scroll detection)
  private persistant: boolean;

  ngOnDestroy() {
    this.stopObserving();
  }

  onScrollIntoView(persistant = false): EventEmitter<void> {
    this.persistant = persistant;

    // IntersectionObserver is not available in SSR
    if (typeof IntersectionObserver !== 'undefined') {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        this.checkForIntersection(entries);
      });

      // setting that only applies to the polyfill, presents unnecessary checking on DOM redraws
      (<any>this._intersectionObserver).USE_MUTATION_OBSERVER = false;

      this._intersectionObserver.observe(<Element>this._element.nativeElement);

      // also watch print command
      this.printSubscription = this.watchForPrintCommand().subscribe(() => {
        if (!this.initialized || this.persistant) {
          this.emit();
        }
      });
    }

    return this._eventEmitter;
  }

  private checkForIntersection = (entries: Array<IntersectionObserverEntry>) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (this.checkIfIntersecting(entry)) {
        this.emit();
      }
    });
  };

  private watchForPrintCommand(): Observable<any> {
    return fromEvent(window, 'beforeprint');
  }

  private checkIfIntersecting(entry: IntersectionObserverEntry) {
    return (<any>entry).isIntersecting && entry.target === this._element.nativeElement;
  }

  private emit() {
    this._eventEmitter.emit();
    this.initialized = true;
    if (!this.persistant) {
      this.stopObserving();
    }
  }

  private stopObserving() {
    if (this._intersectionObserver) {
      this._intersectionObserver.unobserve(<Element>this._element.nativeElement);
      this._intersectionObserver.disconnect();
      this._eventEmitter.complete();
    }

    if (this.printSubscription) {
      this.printSubscription.unsubscribe();
    }
  }
}
